import { BuildSettingsSchema } from './build';
import {
  CarrierVerificationAggregationModeSchema,
  CarrierVerificationPoliciesV2Schema,
  CarrierVerificationPolicySchema,
} from './carrierVerification';
import { CustomFreightTagsSchema } from './customFreightTag';
import { EmailTagSettingsSchema } from './emailTag';
import { GlossarySettingsSchema } from './glossary';
import { AutoReplySettingsSchema } from './originator';
import { RatingMethod } from './pricingStrategy';
import { otherCompanyIDsSchema } from './otherCompanyID';
import { QuoteSettingsSchema } from './quote';
import { RateLimitSchema } from './rateLimit';
import { SopInputResponsiblePartyRoleSchema } from './responsibleParty';
import { CoverageStatus } from './voomaFreight/contractedMovement';
import { z } from 'zod';
import { PolicySuiteSchema } from '../permissions';
import {
  SafeRegexPatternArraySchema,
  SafeRegexPatternSchema,
} from '../validation/regexPattern';

export {
  OrganizationUserRole,
  OrganizationUserRoleSchema,
} from './organizationUserRole';

const UsageIndicatorSchema = z.enum(['TEST', 'PRODUCTION']);
const FrontEmailProviderSchema = z.enum(['OUTLOOK', 'GMAIL']);
const CreatedLocationOwnershipPolicySchema = z.enum([
  'ORGANIZATION',
  'ORIGINATOR',
]);
const FrontSettingsSchema = z.object({
  emailProvider: FrontEmailProviderSchema,
  applicationWebhooksEnabled: z.boolean().nullable().optional(),
});

const PageAccessModeSchema = z.enum([
  'ALL_USERS',
  'ADMINS_ONLY',
  'ADMINS_AND_MANAGERS',
]);

const AccessPoliciesSchema = z.object({
  policySuite: PolicySuiteSchema.nullable().optional(),
  // NOTE(jacob): The three individual settings in this object were implemented before we built full
  // access control. They are still supported, but going forward, the policySuite should be used
  // for access control instead of one-off settings.
  pageAccess: z.object({
    insightsPage: PageAccessModeSchema.default('ALL_USERS'),
  }),
  disableNewCustomerCreation: z.boolean().nullable().optional(),
  usersCanOnlyAccessTheirOwnQuotes: z
    .boolean()
    .default(false)
    .nullable()
    .optional(),
});

const ScheduleOrganizationSettingsSchema = z.object({
  automaticallyApproveSchedulePlans: z.boolean().nullable().optional(),
  automaticallyGenerateSchedulePlans: z.boolean().nullable().optional(),
  outgoingCCRecipients: z.array(z.string()).nullable().optional(),
  autoScheduleAllowListExternalOriginatorIds: z
    .array(z.string())
    .nullable()
    .optional(),
  autoScheduleAllowListPlaceIds: z.array(z.string()).nullable().optional(),
  autoScheduleUserId: z.string().nullable().optional(),
  teamsWebhookUrl: z.string().nullable().optional(),
  // NOTE(jonathan): See comment in ActionDefinition/tools/types.ts
  useOlderAutoScheduleAgent: z.boolean().nullable().optional(),
  batchWindowMinutes: z.number().nullable().optional(),
});

const BuildOrganizationSettingsSchema = BuildSettingsSchema.extend({
  useAgenticBuild: z.boolean().nullable().optional(),
  disableResendToTms: z.boolean().nullable().optional(),
  documentExclusionPatterns: SafeRegexPatternArraySchema.nullable().optional(),
  quoteMatchingEnabled: z.boolean().nullable().optional(),
});

export enum StartTrackingConditionType {
  DRIVER_EXISTS = 'DRIVER_EXISTS',
  MOVEMENT_CARRIER_CONVERSATION_EXISTS = 'MOVEMENT_CARRIER_CONVERSATION_EXISTS',
  READY_FOR_DISPATCH_REACHED = 'READY_FOR_DISPATCH_REACHED',
}

export enum TrackResponsiblePartySource {
  VOOMA_ORIGINATOR_OWNER = 'VOOMA_ORIGINATOR_OWNER',
  FALLBACK_ORDER_OWNER = 'FALLBACK_ORDER_OWNER',
}

export const TMS_RESPONSIBLE_PARTY_LABEL = 'TMS responsible party';

export const TRACK_RESPONSIBLE_PARTY_SOURCE_LABELS: Record<
  TrackResponsiblePartySource,
  string
> = {
  [TrackResponsiblePartySource.VOOMA_ORIGINATOR_OWNER]:
    'Vooma originator owner',
  [TrackResponsiblePartySource.FALLBACK_ORDER_OWNER]:
    'Vooma order owner (if no TMS responsible party)',
};

export const TRACK_RESPONSIBLE_PARTY_SOURCE_OPTIONS: {
  label: string;
  value: TrackResponsiblePartySource | '';
}[] = [
  { label: TMS_RESPONSIBLE_PARTY_LABEL, value: '' },
  ...Object.values(TrackResponsiblePartySource).map((value) => ({
    label: TRACK_RESPONSIBLE_PARTY_SOURCE_LABELS[value],
    value,
  })),
];

export const toTrackResponsiblePartySource = (
  value: unknown
): TrackResponsiblePartySource | null =>
  Object.values(TrackResponsiblePartySource).includes(
    value as TrackResponsiblePartySource
  )
    ? (value as TrackResponsiblePartySource)
    : null;

export const StartTrackingConditionSchema = z.object({
  type: z.nativeEnum(StartTrackingConditionType),
});

export type StartTrackingCondition = z.infer<
  typeof StartTrackingConditionSchema
>;

export const TrackProfileOverrideConditionSchema = z.enum([
  'TEAM_LOAD',
  'PICKUP_WINDOW_AT_MOST_HOURS',
]);

export type TrackProfileOverrideCondition = z.infer<
  typeof TrackProfileOverrideConditionSchema
>;

export type TrackOverrideConditionValueSpec = {
  prefix: string;
  unit: string;
  min: number;
  max: number;
  defaultValue: number;
};

export type TrackOverrideCondition = {
  label: string;
  value?: TrackOverrideConditionValueSpec;
};

export const TRACK_OVERRIDE_CONDITIONS: Record<
  TrackProfileOverrideCondition,
  TrackOverrideCondition
> = {
  TEAM_LOAD: { label: 'Team loads' },
  // NOTE(cara): An appointment (no end, or end equal to start) is a 0-hour
  // window, so a cutoff of 0 targets appointments only.
  PICKUP_WINDOW_AT_MOST_HOURS: {
    label: 'Appointments and short windows',
    value: {
      prefix: 'windows up to',
      unit: 'h',
      min: 0,
      max: 24,
      defaultValue: 3,
    },
  },
};

export const TrackProfileOverridePathSchema = z.enum([
  'enRouteSettings.enRoute.intervalMinutes',
  'nightHoursSettings',
]);

export type TrackProfileOverridePath = z.infer<
  typeof TrackProfileOverridePathSchema
>;

export type TrackOverridableMinutesSetting = {
  kind: 'MINUTES';
  label: string;
  min: number;
  max: number;
  defaultValue: number;
  clearsPaths: string[];
};

export type TrackOverridableRemovalSetting = {
  kind: 'REMOVE';
  label: string;
};

export type TrackOverridableSetting =
  | TrackOverridableMinutesSetting
  | TrackOverridableRemovalSetting;

export const TRACK_OVERRIDABLE_SETTINGS: Record<
  TrackProfileOverridePath,
  TrackOverridableSetting
> = {
  'enRouteSettings.enRoute.intervalMinutes': {
    kind: 'MINUTES',
    label: 'En route check frequency',
    min: 1,
    max: 240,
    defaultValue: 30,
    // NOTE(martin): distance tiers outrank intervalMinutes and fixed daily
    // times are the fallback cadence, so the override must replace both.
    clearsPaths: [
      'enRouteSettings.enRoute.distanceBasedIntervals',
      'enRouteSettings.enRoute.fixedDailyTimes',
    ],
  },
  nightHoursSettings: {
    kind: 'REMOVE',
    label: 'Ignore night hours',
  },
};

function isWithinRange(
  value: number,
  { min, max }: { min: number; max: number }
): boolean {
  return value >= min && value <= max;
}

export const TrackProfileOverrideSchema = z
  .object({
    condition: TrackProfileOverrideConditionSchema,
    conditionValue: z
      .number()
      .int()
      .optional()
      .describe(
        'Parameter for conditions that take one, e.g. the hours cutoff for PICKUP_WINDOW_AT_MOST_HOURS.'
      ),
    path: TrackProfileOverridePathSchema.describe(
      'Resolved track settings path the override writes to, or removes for REMOVE settings.'
    ),
    value: z
      .number()
      .int()
      .optional()
      .describe('Required for MINUTES settings; omitted for REMOVE settings.'),
  })
  .superRefine(({ condition, conditionValue, path, value }, ctx) => {
    const setting = TRACK_OVERRIDABLE_SETTINGS[path];
    if (setting.kind === 'MINUTES') {
      if (value === undefined || !isWithinRange(value, setting)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Value is missing or out of range for this setting',
          path: ['value'],
        });
      }
    } else if (value !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This setting does not take a value',
        path: ['value'],
      });
    }
    const conditionValueSpec = TRACK_OVERRIDE_CONDITIONS[condition].value;
    if (!conditionValueSpec) {
      if (conditionValue !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'This condition does not take a value',
          path: ['conditionValue'],
        });
      }
      return;
    }
    if (
      conditionValue === undefined ||
      !isWithinRange(conditionValue, conditionValueSpec)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Condition value is missing or out of range',
        path: ['conditionValue'],
      });
    }
  });

export type TrackProfileOverride = z.infer<typeof TrackProfileOverrideSchema>;

export function parseTrackProfileOverrides(
  value: unknown
): TrackProfileOverride[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const result = TrackProfileOverrideSchema.safeParse(item);
    return result.success ? [result.data] : [];
  });
}

export const SopInputNoteSourceSchema = z.enum([
  'PICKUP',
  'DELIVERY',
  'CUSTOMER',
]);

export type SopInputNoteSource = z.infer<typeof SopInputNoteSourceSchema>;

export const TrackDashboardPreferencesSchema = z.object({
  hiddenCards: z.array(z.string()).nullable().optional(),
  hiddenSopChecks: z.array(z.string()).nullable().optional(),
  hiddenTotals: z.array(z.string()).nullable().optional(),
});
export type TrackDashboardPreferences = z.infer<
  typeof TrackDashboardPreferencesSchema
>;

export const DEFAULT_TRACK_GEOFENCE_RADIUS_MILES = 1;
export const MIN_TRACK_GEOFENCE_RADIUS_MILES = 0.1;
export const MAX_TRACK_GEOFENCE_RADIUS_MILES = 25;

export const TrackGeofenceSettingsSchema = z.object({
  enabled: z.boolean().nullable().optional(),
  radiusMiles: z
    .number()
    .min(MIN_TRACK_GEOFENCE_RADIUS_MILES)
    .max(MAX_TRACK_GEOFENCE_RADIUS_MILES)
    .nullable()
    .optional(),
});
export type TrackGeofenceSettings = z.infer<typeof TrackGeofenceSettingsSchema>;

export const TrackOrganizationSettingsSchema = z.object({
  stopTrackingOnProviderCompleted: z.boolean().nullable().optional(),
  responsiblePartySource: z
    .nativeEnum(TrackResponsiblePartySource)
    .nullable()
    .optional(),
  sopInputResponsiblePartyRoles: z
    .array(SopInputResponsiblePartyRoleSchema)
    .nullable()
    .optional(),
  sopInputNotes: z
    .array(SopInputNoteSourceSchema)
    .nullable()
    .optional()
    .describe(
      "Notes kept in movement-event SOP input. PICKUP and DELIVERY keep that stop type's notes at movement.movementData.stops[i].notes. CUSTOMER adds the shipment customer's TMS notes as originatorExternalNotes. Unset or empty leaves notes out."
    ),
  // NOTE(jonty): Null/undefined mean enabled at runtime
  trackingIsEnabled: z
    .preprocess((val) => val ?? true, z.boolean())
    .describe(
      'Organization-level on/off switch for track. Takes precedence over any team or originator activation settings'
    ),
  startConditions: z.array(StartTrackingConditionSchema).nullable().optional(),
  evaluateExceptionsWhen: z
    .enum(['ALL', 'NONE', 'TRACKING_EXISTS', 'TRACKING_ABSENT'])
    .nullable()
    .optional()
    .describe(
      'Gates enroute exception evaluation. ALL (default when unset): always evaluate. NONE: never evaluate. TRACKING_EXISTS: only when trackingInfo is set. TRACKING_ABSENT: only when trackingInfo is absent.'
    ),
  useDefaultProfile: z
    .boolean()
    .nullable()
    .optional()
    .describe(
      'When on, customers with no Track profile are tracked with the organization default profile. Off by default, so choosing a default profile does not start tracking those customers.'
    ),
  delayStartMinutes: z
    .number()
    .int()
    .min(0)
    .max(1440)
    .nullable()
    .optional()
    .describe(
      'Minutes to delay the first pre-pickup check after a movement becomes eligible for tracking. Unset means no delay.'
    ),
  calculateEtaToNextStop: z
    .boolean()
    .nullable()
    .optional()
    .describe(
      'When on, en-route milestones also include miles and ETA to the next uncleared stop, alongside the last delivery. Off by default.'
    ),
  routeThroughUnclearedStops: z
    .boolean()
    .nullable()
    .optional()
    .describe(
      'When on (default when unset), the distance to the last delivery is routed through the stops not yet cleared. Turn off for organizations whose TMS does not clear every stop, so the distance is a direct route to the last delivery.'
    ),
  profileOverrides: z
    .array(TrackProfileOverrideSchema)
    .nullable()
    .optional()
    .describe(
      'Org-wide overrides that set a resolved Track profile path when a load matches the condition. Conditions: TEAM_LOAD, or PICKUP_WINDOW_AT_MOST_HOURS with conditionValue as the cutoff in hours (appointments count as 0). nightHoursSettings (no value) ignores night hours for matching loads, so their checks run on normal profile timing at any hour. First match per path wins. Hot loads still use 60 minutes for the en-route interval.'
    ),
  dashboard: TrackDashboardPreferencesSchema.nullable()
    .optional()
    .describe(
      'Cards, SOP checks and totals hidden on the Track dashboard for everyone in the organization. Unset means nothing is hidden.'
    ),
  geofence: TrackGeofenceSettingsSchema.nullable()
    .optional()
    .describe(
      'When enabled, a fresh GPS ping within radiusMiles of the next pickup or delivery adds a comment in the TMS; in Turvo it also sets the status to At pickup / At delivery. Off by default.'
    ),
});

export const MovementEventSettingsSchema = z.object({
  webhookReceivedEnabled: z
    .boolean()
    .nullable()
    .optional()
    .describe(
      'When enabled, a WEBHOOK_RECEIVED movement event is recorded whenever an inbound TMS webhook changes a movement. Off by default.'
    ),
});
export type MovementEventSettings = z.infer<typeof MovementEventSettingsSchema>;

export enum PostedLoadNeedsAttentionRecipient {
  POSTING_USER = 'POSTING_USER',
  SHIPMENT_OWNER = 'SHIPMENT_OWNER',
}
export const PostedLoadNeedsAttentionRecipientSchema = z.nativeEnum(
  PostedLoadNeedsAttentionRecipient
);

export const PostedLoadNeedsAttentionSettingsSchema = z.object({
  hoursToPickup: z.number().int(),
  percentageOfHoursToPickupSincePosting: z.number().min(0).max(1),
  recipient: PostedLoadNeedsAttentionRecipientSchema.nullable().optional(),
});
export type PostedLoadNeedsAttentionSettings = z.infer<
  typeof PostedLoadNeedsAttentionSettingsSchema
>;

export const CarrierInquiriesOnDatPostsSettingsSchema = z.object({
  enabled: z.boolean(),
  intervalMinutes: z.number().int().min(1),
  carrierInquiryActionDefinitionId: z.string().nullable().optional(),
});
export type CarrierInquiriesOnDatPostsSettings = z.infer<
  typeof CarrierInquiriesOnDatPostsSettingsSchema
>;

export const SimulationSettingsSchema = z.object({
  isSimulationBroker: z.boolean().nullable().optional(),
  carrierInquiriesOnDatPosts:
    CarrierInquiriesOnDatPostsSettingsSchema.nullable().optional(),
});
export type SimulationSettings = z.infer<typeof SimulationSettingsSchema>;

export enum TMSIntegrationCheck {
  EQUIPMENT_TYPE = 'EQUIPMENT_TYPE',
  LANE = 'LANE',
  STOP_COUNT = 'STOP_COUNT',
  STOP_DATES = 'STOP_DATES',
}
export const TMSIntegrationCheckSchema = z.nativeEnum(TMSIntegrationCheck);

export enum CanarySchedule {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  SIX_TIMES_DAILY = 'SIX_TIMES_DAILY',
}
export const CanaryScheduleSchema = z.nativeEnum(CanarySchedule);
export enum OutboundCarrierSource {
  LANE_RUNS = 'LANE_RUNS',
}
export const OutboundCarrierSourceSchema = z.nativeEnum(OutboundCarrierSource);

export const OutboundCarrierPreferenceSchema = z.object({
  source: OutboundCarrierSourceSchema,
  amount: z.number().int().min(1),
});

const LocationSettingsSchema = z.object({
  webhookCreatedLocationOwnershipPolicy:
    CreatedLocationOwnershipPolicySchema.nullable().optional(),
  voomaCreatedLocationOwnershipPolicy:
    CreatedLocationOwnershipPolicySchema.nullable().optional(),
});

export enum TransitRouteProvider {
  GOOGLE_MAPS = 'GOOGLE_MAPS',
}

export enum TransitModelingStrategyType {
  MILES_PER_DAY = 'MILES_PER_DAY',
}

export const DEFAULT_TRANSIT_MILES_PER_DAY = 500;

const MilesPerDayTransitModelingStrategySchema = z.object({
  type: z.literal(TransitModelingStrategyType.MILES_PER_DAY),
  milesPerDay: z.number().positive(),
});

export const TransitModelingStrategySchema = z.discriminatedUnion('type', [
  MilesPerDayTransitModelingStrategySchema,
]);

export const TransitModelingRuleSchema = z.object({
  routingProvider: z.nativeEnum(TransitRouteProvider),
  transitModelingStrategy: TransitModelingStrategySchema,
});

export const TransitModelingSettingsSchema = z
  .array(TransitModelingRuleSchema)
  .max(1);

export const DEFAULT_TRANSIT_MODELING_RULE: TransitModelingRule = {
  routingProvider: TransitRouteProvider.GOOGLE_MAPS,
  transitModelingStrategy: {
    type: TransitModelingStrategyType.MILES_PER_DAY,
    milesPerDay: DEFAULT_TRANSIT_MILES_PER_DAY,
  },
};

export const MIN_AUTOMATIC_COVERAGE_CYCLE_INTERVAL_MINUTES = 1;

export enum OutreachChannelSelection {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  ADAPTIVE = 'ADAPTIVE',
}

export const CoverOutreachSettingsSchema = z.object({
  channelSelection: z
    .nativeEnum(OutreachChannelSelection)
    .nullable()
    .optional(),
});
export type CoverOutreachSettings = z.infer<typeof CoverOutreachSettingsSchema>;

export enum CoverRatesCollectionTeamsDestination {
  DM = 'DM',
  GROUP = 'GROUP',
}

export enum CoverRatesCollectionChannel {
  EMAIL = 'EMAIL',
  SLACK = 'SLACK',
  TEAMS = 'TEAMS',
}

export const MissingCoverPricingCollectionSettingsSchema = z
  .object({
    enabled: z.boolean().nullable().optional(),
    channel: z.nativeEnum(CoverRatesCollectionChannel).nullable().optional(),
    teamsDestination: z
      .nativeEnum(CoverRatesCollectionTeamsDestination)
      .nullable()
      .optional(),
    teamsWebhookUrl: z.preprocess(
      (value) =>
        typeof value === 'string' && value.trim() === ''
          ? null
          : typeof value === 'string'
          ? value.trim()
          : value,
      z.string().url().nullable().optional()
    ),
  })
  .superRefine((value, ctx) => {
    const channel = value.channel ?? CoverRatesCollectionChannel.TEAMS;
    if (
      channel === CoverRatesCollectionChannel.TEAMS &&
      value.teamsDestination === CoverRatesCollectionTeamsDestination.GROUP &&
      !value.teamsWebhookUrl
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'teamsWebhookUrl is required when teamsDestination is GROUP',
        path: ['teamsWebhookUrl'],
      });
    }
  });

export enum LoadboardPostingOwnerPolicy {
  POSTING_USER = 'POSTING_USER',
  PRESERVE_EXISTING_OWNER = 'PRESERVE_EXISTING_OWNER',
}

export const LoadboardPostingOwnerPolicySchema = z.nativeEnum(
  LoadboardPostingOwnerPolicy
);

export const ShipmentNoteHidingRuleSchema = z.object({
  substring: z.string().trim().min(1),
  exemptActionDefinitionIds: z.array(z.string().uuid()).optional(),
});

export type ShipmentNoteHidingRule = z.infer<
  typeof ShipmentNoteHidingRuleSchema
>;

function createShipmentNoteHidingRule(
  substring: string
): ShipmentNoteHidingRule {
  return { substring, exemptActionDefinitionIds: [] };
}

export function normalizeShipmentNoteHidingRules(
  rules: Array<string | ShipmentNoteHidingRule> | null | undefined
): ShipmentNoteHidingRule[] | null | undefined {
  return rules?.map((rule) =>
    typeof rule === 'string' ? createShipmentNoteHidingRule(rule) : rule
  );
}

export const COVERAGE_PLAN_STRATEGY_NAMES = [
  'CAPACITY_DRIVEN',
  'CARRIER_LIST_WAVES',
  'COVERAGE_TIERS',
  'EMPTY',
] as const;

export enum AutomaticCoverageMode {
  IMMEDIATE = 'IMMEDIATE',
  SCHEDULED = 'SCHEDULED',
}

export enum CoverageTier {
  BACKHAUL = 'BACKHAUL',
  DELIVERING = 'DELIVERING',
  SAME_LANE = 'SAME_LANE',
  SIMILAR_LANE = 'SIMILAR_LANE',
  LANE_OFFERS = 'LANE_OFFERS',
  EXTERNAL_SOURCES = 'EXTERNAL_SOURCES',
}

export enum ExternalCarrierSource {
  DAT = 'DAT',
  GENLOGS = 'GENLOGS',
  HIGHWAY_LANE_INTELLIGENCE = 'HIGHWAY_LANE_INTELLIGENCE',
}

export const DEFAULT_EXTERNAL_CARRIER_SOURCES = [ExternalCarrierSource.GENLOGS];

export const DEFAULT_COVERAGE_TIER_ORDER: CoverageTier[] = [
  CoverageTier.BACKHAUL,
  CoverageTier.DELIVERING,
  CoverageTier.SAME_LANE,
  CoverageTier.SIMILAR_LANE,
  CoverageTier.LANE_OFFERS,
];

const AUTOMATIC_COVERAGE_RUN_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const AutomaticCoverageScheduleSchema = z.object({
  runTimes: z.array(
    z.string().regex(AUTOMATIC_COVERAGE_RUN_TIME_PATTERN, {
      message: 'Must be a 24-hour time like 10:00.',
    })
  ),
  timezone: z
    .string()
    .trim()
    .min(1)
    .refine((timezone) => isValidIanaTimezone(timezone), {
      message: 'Must be a valid IANA timezone.',
    }),
  pickupWindowDays: z.number().int().min(0),
  // NOTE(rediet): When set, replaces pickupWindowDays with a window ending this
  // many hours after each run, so later runs don't reach further ahead.
  pickupWindowHours: z.number().int().min(1).optional(),
});

// NOTE(bryn): Capped at 25, the same outbound concurrency bound as carrierListWaveSize.
const CoverageWaveBatchSizeSchema = z.number().int().min(1).max(25);

export const UNLIMITED_CAPACITY_WAVE_SIZE = 'UNLIMITED' as const;

const CapacityDrivenWaveSizeSchema = z.union([
  CoverageWaveBatchSizeSchema,
  z.literal(UNLIMITED_CAPACITY_WAVE_SIZE),
]);

const CapacityDrivenMaxCarriersSchema = z.union([
  z.number().int().min(1),
  z.literal(UNLIMITED_CAPACITY_WAVE_SIZE),
]);

const CoverageWaveSettingsSchema = z.object({
  batchSize: CoverageWaveBatchSizeSchema.nullable().optional(),
  waitMinutes: z.number().int().min(1).nullable().optional(),
});

const InNetworkTierOrderSchema = z
  .array(z.nativeEnum(CoverageTier))
  .min(1)
  .refine((tiers) => new Set(tiers).size === tiers.length, {
    message: 'Each tier can appear only once.',
  });

const ExternalCarrierSourcesSchema = z
  .array(z.nativeEnum(ExternalCarrierSource))
  .min(1)
  .transform((sources) =>
    sources.filter((source, index) => sources.indexOf(source) === index)
  );

export const CoveragePlanStrategySettingsSchema = z.object({
  CAPACITY_DRIVEN: z
    .object({
      waveSize: CapacityDrivenWaveSizeSchema.nullable().optional(),
      maxCarriers: CapacityDrivenMaxCarriersSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
  COVERAGE_TIERS: CoverageWaveSettingsSchema.extend({
    tierOrder: z.array(z.nativeEnum(CoverageTier)).nullable().optional(),
    externalSources: ExternalCarrierSourcesSchema.nullable().optional(),
  })
    .nullable()
    .optional(),
});

const ShipmentNoteHidingRuleInputSchema = z.preprocess(
  (rule) =>
    typeof rule === 'string' ? createShipmentNoteHidingRule(rule) : rule,
  ShipmentNoteHidingRuleSchema
);

// NOTE(rediet): GraphQL Int is 32-bit; larger values fail to serialize.
const GRAPHQL_INT_MAX = 2_147_483_647;

export const CoverSettingsSchema = z.object({
  outreach: CoverOutreachSettingsSchema.nullable().optional(),
  testPhoneNumberForOutboundCalls: z.string().nullable().optional(),
  testEmailForOutboundEmails: z.string().nullable().optional(),
  rateConfirmationFallbackEmail: z.string().email().nullable().optional(),
  defaultMcLeodLoadboardUser: z.string().nullable().optional(),
  availableLoadBoards: z.array(z.string()).nullable().optional(),
  continuePendingTransfersOnHangup: z.boolean().nullable().optional(),
  autoRefreshLoadboardPostings: z.boolean().nullable().optional(),
  automaticCoverageEnabled: z.boolean().nullable().optional(),
  // NOTE(rediet): Deprecated and unread; kept so stored values still validate.
  automaticCoverageCycleIntervalMinutes: z
    .number()
    .min(MIN_AUTOMATIC_COVERAGE_CYCLE_INTERVAL_MINUTES)
    .nullable()
    .optional(),
  coveragePlanStrategy: z
    .enum(COVERAGE_PLAN_STRATEGY_NAMES)
    .nullable()
    .optional(),
  coveragePlanStrategySettings:
    CoveragePlanStrategySettingsSchema.nullable().optional(),
  automaticCoverageMode: z
    .nativeEnum(AutomaticCoverageMode)
    .nullable()
    .optional(),
  automaticCoverageSchedule:
    AutomaticCoverageScheduleSchema.nullable().optional(),
  // NOTE(dev): Target max is 100. Capped at 25 until the outbound concurrency limit (CONCURRENT_LIMIT_FOR_OUTBOUND_CALLS) is raised; raise both together.
  carrierListWaveSize: z.number().int().min(1).max(25).nullable().optional(),
  laneHistoryRadiusMiles: z
    .number()
    .int()
    .positive()
    .max(GRAPHQL_INT_MAX)
    .nullable()
    .optional(),
  targetPriceOptions: z
    .object({
      percentageOverTarget: z.number(),
      minMarginOverTarget: z.number().nullable().optional(),
      maxMarginOverTarget: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  carrierVerificationPolicies: z
    .union([
      z.array(CarrierVerificationPolicySchema),
      CarrierVerificationPoliciesV2Schema,
    ])
    .nullable()
    .optional(),
  carrierVerificationAggregationMode:
    CarrierVerificationAggregationModeSchema.nullable().optional(),
  skipRmisExpandedLookupWhenAlreadyRegistered: z
    .boolean()
    .nullable()
    .optional(),
  negotiationGuidance: z.string().nullable().optional(),
  originatorIdsToHideShipmentsFor: z.array(z.string()).nullable().optional(),
  notesSubstringsToHideShipmentsFor: z
    .array(ShipmentNoteHidingRuleInputSchema)
    .nullable()
    .optional(),
  originatorIdsToBlockPostingShipmentsFor: z
    .array(z.string())
    .nullable()
    .optional(),
  disablePostMaskedMovement: z.boolean().nullable().optional(),
  showPastLoads: z.boolean().nullable().optional(),
  coverageStatusesToHideFromCommandCenter: z
    .array(z.nativeEnum(CoverageStatus))
    .nullable()
    .optional(),
  // NOTE(cara): Deprecated name for coverageStatusesToHideFromCommandCenter.
  // Kept so app versions that still read it keep working until cleanup.
  tmsStatusesToHideFromCommandCenter: z
    .array(z.nativeEnum(CoverageStatus))
    .nullable()
    .optional(),
  nonSellableTmsStatuses: z
    .array(z.nativeEnum(CoverageStatus))
    .nullable()
    .optional(),
  postedLoadNeedsAttentionSettings:
    PostedLoadNeedsAttentionSettingsSchema.nullable().optional(),
  tmsIntegrationChecks: z
    .array(TMSIntegrationCheckSchema)
    .nullable()
    .optional(),
  canaryEnabled: z.boolean().nullable().optional(),
  canarySchedule: CanaryScheduleSchema.nullable().optional(),
  outboundCarrierPreferences: z
    .array(OutboundCarrierPreferenceSchema)
    .nullable()
    .optional(),
  sellWithoutPricingReferencePatterns:
    SafeRegexPatternArraySchema.nullable().optional(),
  carrierOfferRecording: z
    .object({
      reduceReferencesToNumerics: z.boolean().nullable().optional(),
      defaultMissingFieldsToUnknown: z.boolean().nullable().optional(),
    })
    .nullable()
    .optional(),
  postPickupDatRefreshGracePeriodMinutes: z
    .number()
    .positive()
    .nullable()
    .optional(),
  missingCoverPricingCollection:
    MissingCoverPricingCollectionSettingsSchema.nullable().optional(),
});

export const OrganizationCoverSettingsSchema = CoverSettingsSchema.extend({
  loadboardPostingOwnerPolicy:
    LoadboardPostingOwnerPolicySchema.nullable().optional(),
  compactDATPostingHoursEnabled: z.boolean().nullable().optional(),
  afterHoursAlwaysOnForAllTeams: z.boolean().nullable().optional(),
});

export enum CoverAutomaticCoverageStrategy {
  CAPACITY_DRIVEN = 'CAPACITY_DRIVEN',
  CARRIER_LIST_WAVES = 'CARRIER_LIST_WAVES',
  COVERAGE_TIERS = 'COVERAGE_TIERS',
  EMPTY = 'EMPTY',
}

export const CoverAutomaticCoverageStrategySchema = z.nativeEnum(
  CoverAutomaticCoverageStrategy
);

export const CoverAutomaticCoverageSettingsSchema = CoverSettingsSchema.pick({
  automaticCoverageEnabled: true,
  automaticCoverageMode: true,
  automaticCoverageSchedule: true,
  carrierListWaveSize: true,
}).extend({
  capacityDrivenWaveSize: CapacityDrivenWaveSizeSchema.nullable().optional(),
  capacityDrivenMaxCarriers:
    CapacityDrivenMaxCarriersSchema.nullable().optional(),
  coveragePlanStrategy: z.string().nullable().optional(),
  inNetworkTierOrder: InNetworkTierOrderSchema.nullable().optional(),
  externalCarrierSources: ExternalCarrierSourcesSchema.nullable().optional(),
});

export const RunnableAutomaticCoverageScheduleSchema =
  AutomaticCoverageScheduleSchema.extend({
    runTimes: AutomaticCoverageScheduleSchema.shape.runTimes.nonempty({
      message: 'Add at least one run time.',
    }),
  });

export const CoverAutomaticCoverageSettingsUpdateSchema =
  CoverAutomaticCoverageSettingsSchema.extend({
    automaticCoverageSchedule:
      RunnableAutomaticCoverageScheduleSchema.nullable().optional(),
    coveragePlanStrategy:
      CoverAutomaticCoverageStrategySchema.nullable().optional(),
  }).strict();

export function sanitizeStoredCoverAutomaticCoverageSettings(
  storedSettings: unknown
): CoverAutomaticCoverageSettings {
  if (
    typeof storedSettings !== 'object' ||
    storedSettings === null ||
    Array.isArray(storedSettings)
  ) {
    return {};
  }

  const settings: Record<string, unknown> = {
    capacityDrivenWaveSize: getStoredCapacityDrivenSetting(
      storedSettings,
      'waveSize'
    ),
    capacityDrivenMaxCarriers: getStoredCapacityDrivenSetting(
      storedSettings,
      'maxCarriers'
    ),
    inNetworkTierOrder: getStoredInNetworkTierOrder(storedSettings),
    externalCarrierSources: getStoredExternalCarrierSources(storedSettings),
    ...storedSettings,
  };
  return Object.fromEntries(
    Object.entries(CoverAutomaticCoverageSettingsSchema.shape).flatMap(
      ([key, schema]) => {
        const storedValue = settings[key];
        if (storedValue === undefined) return [];
        const result = schema.safeParse(storedValue);
        return result.success ? [[key, result.data]] : [];
      }
    )
  ) as CoverAutomaticCoverageSettings;
}

function getStoredCapacityDrivenSetting(
  storedSettings: object,
  key: 'waveSize' | 'maxCarriers'
): unknown {
  const parsed = z
    .object({
      coveragePlanStrategySettings: z
        .object({
          CAPACITY_DRIVEN: z
            .object({ waveSize: z.unknown(), maxCarriers: z.unknown() })
            .nullable()
            .optional(),
        })
        .nullable()
        .optional(),
    })
    .safeParse(storedSettings);
  if (!parsed.success) return undefined;
  return parsed.data.coveragePlanStrategySettings?.CAPACITY_DRIVEN?.[key];
}

function getStoredExternalCarrierSources(storedSettings: object): unknown {
  const storedSettingsSchema = z
    .object({
      coveragePlanStrategySettings: z
        .object({
          COVERAGE_TIERS: z
            .object({ externalSources: z.unknown().nullable().optional() })
            .nullable()
            .optional(),
        })
        .nullable()
        .optional(),
    })
    .passthrough();
  const parsed = storedSettingsSchema.safeParse(storedSettings);
  if (!parsed.success) return undefined;
  return parsed.data.coveragePlanStrategySettings?.COVERAGE_TIERS
    ?.externalSources;
}

function getStoredInNetworkTierOrder(storedSettings: object): unknown {
  const { coveragePlanStrategySettings } = storedSettings as {
    coveragePlanStrategySettings?: { COVERAGE_TIERS?: { tierOrder?: unknown } };
  };
  return coveragePlanStrategySettings?.COVERAGE_TIERS?.tierOrder;
}

export enum MaybeReferencesScope {
  SUBJECT_ONLY = 'SUBJECT_ONLY',
  ANYWHERE = 'ANYWHERE',
}

export const MaybeReferencesScopeSchema = z.nativeEnum(MaybeReferencesScope);

const ThreadAssociationSettingsSchema = z.object({
  keywords: z.array(z.string()).nullable().optional(),
  // NOTE(martin): Unset scopes default to SUBJECT_ONLY, independent of the
  // org-level maybeReferencesScope used for inbox scanning.
  keywordScope: MaybeReferencesScopeSchema.nullable().optional(),
  referenceScope: MaybeReferencesScopeSchema.nullable().optional(),
  // NOTE(martin): Blank/unset matches all senders. Non-empty restricts thread
  // association creation to messages from one of these addresses.
  fromAddresses: z.array(z.string()).nullable().optional(),
});

const CarrierThreadSettingsSchema = ThreadAssociationSettingsSchema.extend({
  invalidateThreadsWhenCarrierChanges: z.boolean().nullable().optional(),
});

const ShipperThreadSettingsSchema = ThreadAssociationSettingsSchema.extend({
  // NOTE(martin): Lets an originator contact (exact or *@domain) on the email
  // stand in for a keyword match; a load reference is still required.
  matchOriginatorContacts: z.boolean().nullable().optional(),
  requireTrackTeamMember: z.boolean().nullable().optional(),
});

const EmailScanningSettingsSchema = z.object({
  enabled: z.boolean(),
  allowList: z.array(z.string()),
  blockList: z.array(z.string()),
  allowContacts: z.boolean().nullable().optional(),
  addTaskIdToOutgoingEmails: z.boolean().nullable().optional(),
  processingLimit: z.number().nullable().optional(),
  maybeReferencesScope: MaybeReferencesScopeSchema.nullable().optional(),
  // NOTE(jonty): Blank/unset keeps historical TMS defaults (Turvo 12345-67890,
  // McLeod/Aljex 7-digit). Only a non-empty safe regex overrides that default.
  maybeReferencesPattern: z.preprocess(
    (value) =>
      typeof value === 'string' && value.trim() === '' ? null : value,
    SafeRegexPatternSchema.nullable().optional()
  ),
  carrierThread: CarrierThreadSettingsSchema.nullable().optional(),
  shipperThread: ShipperThreadSettingsSchema.nullable().optional(),
});

const SpamGuardSettingsSchema = z.object({
  phoneCallWaitPeriod: z.number().nullable().optional(),
});

const TransferSettingsSchema = z.object({
  dtfmPinLength: z.number().nullable().optional(),
});

const EscalationSuppressionPeriodItemSchema = z.object({
  default: z.number(),
});

const EscalationSuppressionPeriodSettingsSchema = z.object({
  GENERAL: EscalationSuppressionPeriodItemSchema.nullable().optional(),
});

const EscalationsSettingsSchema = z.object({
  transfer: TransferSettingsSchema.nullable().optional(),
});

const RecordingSettingsSchema = z.object({
  recordCalls: z.boolean(),
  recordingAnnouncement: z.string(),
});

const TelephonySettingsSchema = z.object({
  recordingSettings: RecordingSettingsSchema.nullable().optional(),
  backgroundAmbienceVolume: z.number().min(0).max(1).nullable().optional(),
});

export const NotificationChannelTypeSchema = z.enum([
  'EMAIL',
  'MS_TEAMS',
  'SLACK',
]);
export type NotificationChannelType = z.infer<
  typeof NotificationChannelTypeSchema
>;

export const namedNotifications = [
  'carrierOfferOnMyLoad',
  'carrierOfferOnMyOriginatorsLoad',
  'failedTransfer',
  'postedLoadIsCold',
  'postedLoadIsMispriced',
  'testNotification',
  'carrierVerificationIntegrationVerifierFailure',
  'tmsIntegrationVerifierFailure',
  'tmsIntegrationVerifierSuccess',
  'transferSummary',
  'transferSummaryToTeam',
  'shipmentCoverageSummary',
  'shipmentCoverageSummaryToTeam',
] as const;
export type NamedNotification = (typeof namedNotifications)[number];
const NotificationChannelSchema = z.object({
  type: NotificationChannelTypeSchema,
  externalIdentifier: z.string().optional().nullable(),
});
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

const NotificationSettingSchema = z.object({
  channels: z.array(NotificationChannelSchema),
});
export type NotificationSetting = z.infer<typeof NotificationSettingSchema>;

const TransferSummaryNotificationSettingSchema =
  NotificationSettingSchema.extend({
    skipSendSummaryToShipmentOwner: z.boolean().nullable().optional(),
  });
export type TransferSummaryNotificationSetting = z.infer<
  typeof TransferSummaryNotificationSettingSchema
>;

export const DEFAULT_MAX_COVERAGE_SUMMARY_NOTIFICATIONS_PER_HOUR = 5;

const ShipmentCoverageSummaryNotificationSettingSchema =
  NotificationSettingSchema.extend({
    sendSummaryOnEveryInquiry: z.boolean().nullable().optional(),
    maxNotificationsPerShipmentPerHour: z
      .number()
      .int()
      .min(1)
      .max(100)
      .nullable()
      .optional(),
  });
export type ShipmentCoverageSummaryNotificationSetting = z.infer<
  typeof ShipmentCoverageSummaryNotificationSettingSchema
>;

const EXTENDED_NOTIFICATIONS = [
  'transferSummary',
  'shipmentCoverageSummary',
] as const;

const notificationSettingsShape = {
  ...Object.fromEntries(
    namedNotifications
      .filter(
        (n) =>
          !EXTENDED_NOTIFICATIONS.includes(
            n as (typeof EXTENDED_NOTIFICATIONS)[number]
          )
      )
      .map((n) => [n, NotificationSettingSchema.nullable().optional()])
  ),
  transferSummary:
    TransferSummaryNotificationSettingSchema.nullable().optional(),
  shipmentCoverageSummary:
    ShipmentCoverageSummaryNotificationSettingSchema.nullable().optional(),
} as {
  [K in Exclude<
    NamedNotification,
    (typeof EXTENDED_NOTIFICATIONS)[number]
  >]: z.ZodOptional<z.ZodNullable<typeof NotificationSettingSchema>>;
} & {
  transferSummary: z.ZodOptional<
    z.ZodNullable<typeof TransferSummaryNotificationSettingSchema>
  >;
  shipmentCoverageSummary: z.ZodOptional<
    z.ZodNullable<typeof ShipmentCoverageSummaryNotificationSettingSchema>
  >;
};

export const NotificationSettingsSchema = z
  .object(notificationSettingsShape)
  .strict();

const PollingTypeSchema = z.enum(['orders', 'shipments', 'tms_comments']);

export const OrganizationAutoReplySettingsSchema =
  AutoReplySettingsSchema.extend({
    alwaysOnDraftingInstructions: z
      .string()
      .optional()
      .nullable()
      .describe(
        'AI instructions that will always be added regardless of team, user or customer settings.'
      ),
    sendDelaySeconds: z
      .number()
      .int()
      .min(0)
      .optional()
      .nullable()
      .describe('Delay before sending a quote auto-reply, in seconds.'),
  }).strict();

const RetentionPeriodSettingsSchema = z.object({
  calls: z.number().nullable().optional(),
  jobs: z.number().nullable().optional(),
});

const PylonSettingsSchema = z.object({
  accountId: z.string(),
});

export const CarrierInterestContextSettingsSchema = z.object({
  enabledRateGuidanceMethods: z
    .array(
      z.enum(Object.values(RatingMethod) as [RatingMethod, ...RatingMethod[]])
    )
    .default([]),
});

export const isValidIanaTimezone = (timezone: string): boolean => {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

export const OrganizationDefaultTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine(isValidIanaTimezone, {
    message: 'Must be a valid IANA timezone.',
  });

export const OrganizationSettingsSchema = z.object({
  locations: LocationSettingsSchema.nullable().optional(),
  glossary: GlossarySettingsSchema.nullable().optional(),
  emailScanning: EmailScanningSettingsSchema.nullable().optional(),
  front: FrontSettingsSchema.nullable().optional(),
  schedule: ScheduleOrganizationSettingsSchema.nullable().optional(),
  track: TrackOrganizationSettingsSchema.nullable().optional(),
  cover: OrganizationCoverSettingsSchema.nullable().optional(),
  movementEvents: MovementEventSettingsSchema.nullable().optional(),
  build: BuildOrganizationSettingsSchema.nullable().optional(),
  buildShipmentFromExternalDataInstructions: z.string().nullable().optional(),
  teamsWebhookUrl: z.string().nullable().optional(),
  quoteAutoReply: OrganizationAutoReplySettingsSchema.nullable().optional(),
  emailTags: EmailTagSettingsSchema.nullable().optional(),
  accessPolicies: AccessPoliciesSchema.nullable().optional(),
  customFreightTags: CustomFreightTagsSchema.nullable().optional(),
  spamGuard: SpamGuardSettingsSchema.nullable().optional(),
  escalations: EscalationsSettingsSchema.nullable().optional(),
  escalationSuppressionPeriod:
    EscalationSuppressionPeriodSettingsSchema.nullable().optional(),
  transfersRateLimit: RateLimitSchema.nullable().optional(),
  polling: z.array(PollingTypeSchema).nullable().optional(),
  pollStopChanges: z.boolean().nullable().optional(),
  pollingIntervalMinutes: z
    .number()
    .int()
    .min(1)
    .max(1440)
    .nullable()
    .optional(),
  otherCompanyIDs: otherCompanyIDsSchema.nullable().optional(),
  notifications: NotificationSettingsSchema.nullable().optional(),
  quote: QuoteSettingsSchema.nullable().optional(),
  carrierInterestContext:
    CarrierInterestContextSettingsSchema.nullable().optional(),
  telephony: TelephonySettingsSchema.nullable().optional(),
  retentionPeriodHours: RetentionPeriodSettingsSchema.nullable().optional(),
  pylon: PylonSettingsSchema.nullable().optional(),
  simulation: SimulationSettingsSchema.nullable().optional(),
  transitModeling: TransitModelingSettingsSchema.nullable().optional(),
  operatingName: z.string().nullable().optional(),
  pronounceableOperatingName: z.string().nullable().optional(),
  mcNumber: z.string().nullable().optional(),
  defaultTimezone: z.preprocess(
    (value) =>
      typeof value === 'string' && value.trim() === ''
        ? null
        : typeof value === 'string'
        ? value.trim()
        : value,
    OrganizationDefaultTimezoneSchema.nullable().optional()
  ),
});

export type OrganizationAutoReplySettings = z.infer<
  typeof OrganizationAutoReplySettingsSchema
>;

export type FrontProvider = z.infer<typeof FrontEmailProviderSchema>;
export type UsageIndicator = z.infer<typeof UsageIndicatorSchema>;
export type CreatedLocationOwnershipPolicy = z.infer<
  typeof CreatedLocationOwnershipPolicySchema
>;
export type PageAccessMode = z.infer<typeof PageAccessModeSchema>;
export type AccessPolicies = z.infer<typeof AccessPoliciesSchema>;
export type FrontSettings = z.infer<typeof FrontSettingsSchema>;
export type LocationSettings = z.infer<typeof LocationSettingsSchema>;
export type CoverSettings = z.infer<typeof CoverSettingsSchema>;
export type CoverAutomaticCoverageSettings = z.infer<
  typeof CoverAutomaticCoverageSettingsSchema
>;
export type CoverAutomaticCoverageSettingsUpdate = z.infer<
  typeof CoverAutomaticCoverageSettingsUpdateSchema
>;
export type AutomaticCoverageSchedule = z.infer<
  typeof AutomaticCoverageScheduleSchema
>;
export type CoveragePlanStrategySettings = z.infer<
  typeof CoveragePlanStrategySettingsSchema
>;
export type OrganizationCoverSettings = z.infer<
  typeof OrganizationCoverSettingsSchema
>;
export type MissingCoverPricingCollectionSettings = z.infer<
  typeof MissingCoverPricingCollectionSettingsSchema
>;
export type EmailScanningSettings = z.infer<typeof EmailScanningSettingsSchema>;
export type ThreadAssociationSettings = z.infer<
  typeof ThreadAssociationSettingsSchema
>;
export type CarrierThreadSettings = z.infer<typeof CarrierThreadSettingsSchema>;
export type ShipperThreadSettings = z.infer<typeof ShipperThreadSettingsSchema>;
export type SpamGuardSettings = z.infer<typeof SpamGuardSettingsSchema>;
export type ScheduleOrganizationSettings = z.infer<
  typeof ScheduleOrganizationSettingsSchema
>;
export type BuildOrganizationSettings = z.infer<
  typeof BuildOrganizationSettingsSchema
>;
export type TrackOrganizationSettings = Omit<
  z.infer<typeof TrackOrganizationSettingsSchema>,
  'trackingIsEnabled'
> & {
  trackingIsEnabled?: boolean | null;
};

export {
  DEFAULT_TRACK_ESCALATION_EMAIL_INSTRUCTIONS,
  DEFAULT_TRACK_ESCALATION_RULE_ID,
  TRACK_ESCALATE_SOP_INSTRUCTIONS_PROMPT_VALUE_KEY,
  TRACK_ESCALATE_SOP_TAG,
  TRACK_ESCALATION_CHANNEL_LABELS,
  TRACK_ESCALATION_METHOD_LABELS,
  TRACK_ESCALATION_RULES_PROMPT_VALUE_KEY,
  TrackEscalationMethod,
  TrackEscalationRecipientType,
  TrackEscalationRuleSchema,
  buildTrackEscalateSopPrompt,
  createEmptyTrackEscalationChannel,
  createEmptyTrackEscalationRule,
  defaultTrackEscalationRule,
  parseTrackEscalationRules,
  parseTrackEscalationRulesFromSopValues,
  rulesOrEmailDefault,
  serializeTrackEscalationRulesForSop,
  specificEmailRecipient,
} from './trackEscalation';
export type {
  TrackEscalationChannel,
  TrackEscalationRule,
} from './trackEscalation';
export type TransferSettings = z.infer<typeof TransferSettingsSchema>;
export type EscalationSuppressionPeriodItem = z.infer<
  typeof EscalationSuppressionPeriodItemSchema
>;
export type EscalationSuppressionPeriodSettings = z.infer<
  typeof EscalationSuppressionPeriodSettingsSchema
>;
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;
export type EscalationsSettings = z.infer<typeof EscalationsSettingsSchema>;
export type PollingType = z.infer<typeof PollingTypeSchema>;
export type TelephonySettings = z.infer<typeof TelephonySettingsSchema>;
export type RecordingSettings = z.infer<typeof RecordingSettingsSchema>;
export type OrganizationSettings = z.infer<typeof OrganizationSettingsSchema>;
export type CarrierInterestContextSettings = z.infer<
  typeof CarrierInterestContextSettingsSchema
>;
export type OutboundCarrierPreference = z.infer<
  typeof OutboundCarrierPreferenceSchema
>;
export type RetentionPeriodSettings = z.infer<
  typeof RetentionPeriodSettingsSchema
>;
export type PylonSettings = z.infer<typeof PylonSettingsSchema>;
export type TransitModelingStrategy = z.infer<
  typeof TransitModelingStrategySchema
>;
export type TransitModelingRule = z.infer<typeof TransitModelingRuleSchema>;
export type TransitModelingSettings = z.infer<
  typeof TransitModelingSettingsSchema
>;
